import React, { ChangeEvent, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Stack,
  Typography,
  styled,
  InputAdornment,
  IconButton,
  Dialog,
  Divider
} from '@mui/material';
import { TableCustom } from 'app/components/elements/TableCustom';
import dayjs from 'dayjs';
import { ActivityItem, useActivityInjection } from './hook';
import { InputText } from '../../components/elements/InputText';
import { InputSelect } from '../../components/elements/InputSelect';
import { SelectChangeEvent } from '@mui/material/Select';
import { fieldPathToLabel } from 'utils/diff';
import get from 'lodash/get';
import { DatePicker } from '../../components/elements/DatePicker';
import { SearchType } from 'types/loctions';
const HIDDEN_FIELDS = ['id', 'projectId', 'createdAt', 'updatedAt', 'deletedAt'];
const optionSearchType = [
  { label: 'Partial Text', value: 'partial-text' },
  { label: 'Full text', value: 'full-text' }
];
export const ActivityPage = () => {
  const {
    activities,
    pagingInfo,
    handleChangePage,
    searchText,
    setSearchText,
    users,
    userSelect,
    setUserSelect,
    itemTypeFilter,
    openModal,
    setOpenModal,
    setItemTypeFilter,
    convertDiff,
    selectedItem,
    handleOpenModal,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    searchType,
    setSearchType
  } = useActivityInjection();
  const navigate = useNavigate();
  const handleCloseModal = () => setOpenModal(false);

  const headers = [
    { label: 'Timestamp', width: 220 },
    { label: 'User', width: 120 },
    { label: 'Item Type', width: 120 },
    { label: 'Item Name/ID', width: 180 },
    { label: 'View Details', width: 120 }
  ];

  const renderName = useCallback((item: ActivityItem | undefined) => {
    if (!item) return '-';
    switch (item.type) {
      case 'ISSUE':
        return item.issue?.name;
      case 'ROOM':
      case 'ROOM_ISSUE':
        return item.submitted?.room?.name || '-';
      case 'ASSET':
        return item.asset?.name || '-';
      case 'ADHOC_ISSUE':
        return (
          item.adHocIssue?.name ||
          item.adHocIssue?.description ||
          item.adHocIssue?.locationPathString ||
          '-'
        );
      case 'CLIENT':
        return item.client?.clientName || '-';
      default:
        return '-';
    }
  }, []);

  const dataTable = activities.map((item) => ({
    timestamp: {
      value: dayjs(get(item, 'stack[0].createdAt')).format('MM/DD/YYYY, hh:mm A')
    },
    user: { value: get(item, 'stack[0].user.name') },
    itemType: {
      value: (() => {
        const raw = get(item, 'stack[0].type', '');
        if (raw === 'ROOM') return 'LOCATION';
        if (raw === 'ROOM_ISSUE') return 'LOCATION ISSUE';
        if (raw === 'ADHOC_ISSUE') return 'ADHOC ISSUE';
        if (raw === 'CLIENT') return 'CLIENT';
        return raw;
      })()
    },
    itemName: {
      value: (() => {
        const first = get(item, 'stack[0]') as ActivityItem | undefined;
        if (first?.type === 'ADHOC_ISSUE' && first?.adHocIssue?.id) {
          const id = first.adHocIssue.id;
          const label = first.adHocIssue.name || `Adhoc Issue #${id}`;
          return (
            <Typography
              sx={{
                color: '#0056D2',
                fontWeight: 500,
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
              onClick={() => {
                navigate(`/issues?tab=adHocIssues&adHocIssueId=${id}`);
              }}>
              {label}
            </Typography>
          );
        }
        if (first?.type === 'CLIENT' && first?.client?.clientName) {
          return first.client.clientName;
        }
        return renderName(first);
      })()
    },
    viewDetail: {
      value: (
        <Stack flexDirection={'row'} alignItems={'center'} gap={1}>
          <Typography
            sx={{ color: '#0056D2', fontWeight: 500, cursor: 'pointer' }}
            onClick={() => handleOpenModal(item)}>
            View Details
          </Typography>
        </Stack>
      )
    }
  }));

  return (
    <WrapperPage>
      <TypographyTitle>Activity Log</TypographyTitle>

      {/* Search + Filter */}
      <Stack>
        <Stack flexDirection={'row'} gap={1.25} flexWrap="wrap" alignItems="flex-end">
          <Stack gap={1}>
            <InputText
              startAdornment={
                <InputAdornment position="end">
                  <IconButton>
                    <img alt={'icon-user'} src={'/icons/icon_search.svg'} width={24} />
                  </IconButton>
                </InputAdornment>
              }
              placeholder={'Search'}
              inputProps={{
                value: searchText,
                style: { width: '320px', minHeight: '56px', height: '56px', padding: 0 },
                onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  setSearchText(event.target.value);
                }
              }}
            />
          </Stack>
          <Stack width={'200px'} minWidth={220}>
            <InputSelect
              value={userSelect || ''}
              placeholder={'User'}
              onChange={(e: SelectChangeEvent<unknown>) => {
                const val = e.target.value as string;
                setUserSelect(val === '' ? undefined : val);
              }}
              sx={{ minHeight: '56px', height: '56px', padding: 0 }}
              options={[
                { label: 'All', value: '' },
                ...(users?.map((item) => ({
                  label: item.label,
                  value: item.value
                })) ?? [])
              ]}
            />
          </Stack>
          <Stack width={'200px'} minWidth={220}>
            <InputSelect
              value={itemTypeFilter || ''}
              placeholder={'Item Type'}
              onChange={(e: SelectChangeEvent<unknown>) => {
                const val = e.target.value as string;
                setItemTypeFilter(val === '' ? undefined : val);
              }}
              sx={{ minHeight: '56px', height: '56px', padding: 0 }}
              options={[
                { label: 'All', value: '' },
                { label: 'LOCATION', value: 'ROOM' },
                { label: 'ASSET', value: 'ASSET' },
                { label: 'ISSUE', value: 'ISSUE' },
                { label: 'LOCATION ISSUE', value: 'ROOM_ISSUE' },
                { label: 'ADHOC ISSUE', value: 'ADHOC_ISSUE' },
                { label: 'CLIENT', value: 'CLIENT' }
              ]}
            />
          </Stack>
        </Stack>
        <Stack flexDirection={'row'} gap={1.25} flexWrap="wrap" alignItems="flex-end">
          <Stack width={'200px'} minWidth={220}>
            <DatePicker
              title={'From Date'}
              value={fromDate ? dayjs(fromDate) : null}
              onChange={(val) => setFromDate(dayjs(val).toDate())}
            />
          </Stack>
          <Stack width={'200px'} minWidth={220}>
            <DatePicker
              title={'To Date'}
              value={toDate ? dayjs(toDate) : null}
              onChange={(val) => setToDate(dayjs(val).toDate())}
            />
          </Stack>
          <Stack style={{ width: 'fit-content', gap: '8px' }}>
            <Typography
              style={{
                fontWeight: '700',
                color: '#226AAA',
                whiteSpace: 'nowrap'
              }}>
              Search Type
            </Typography>
            <InputSelect
              value={searchType}
              onChange={(e) => setSearchType(e.target.value as SearchType)}
              options={optionSearchType}
            />
          </Stack>
        </Stack>
      </Stack>

      {/* Table */}
      <TableCustom
        handleChooseRow={() => {}}
        headers={headers}
        data={dataTable}
        isScrollX
        styledTableRow={{ cursor: 'default' }}
        pagingInfo={pagingInfo}
        handleChangePage={handleChangePage}
      />

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: '12px', padding: '20px' } }}>
        {(() => {
          const firstLog = selectedItem?.stack?.[0];
          const isAdHocIssue = firstLog?.type === 'ADHOC_ISSUE';
          const isClientActivity = firstLog?.type === 'CLIENT';

          const uniqueFields = selectedItem
            ? Array.from(
                new Set(
                  selectedItem?.stack?.flatMap((log) =>
                    convertDiff(log)
                      .filter((d) => !HIDDEN_FIELDS.includes(d.field))
                      .map((d) => d.field)
                  )
                )
              )
            : [];

          return (
            <Stack gap={2} minHeight={'600px'} width={'fit-content'}>
              <Typography fontWeight={700} fontSize={18}>
                Changes Overview / Difference Summary
              </Typography>
              <Divider />
              <Stack width="100%" gap={2} sx={{ overflowX: 'auto' }}>
                {isAdHocIssue || isClientActivity ? (
                  <>
                    <Stack direction="row" sx={{ fontWeight: 700, minWidth: '800px' }}>
                      <Typography width={200}>Date of Change</Typography>
                      <Typography width={200}>User</Typography>
                      <Typography width={240}>Client / Location</Typography>
                      <Typography width={160}>Action</Typography>
                    </Stack>
                    {selectedItem?.stack?.map((log) => {
                      const actionLabelMap: Record<string, string> = {
                        CLIENT_ARCHIVED: 'Client Archived',
                        CLIENT_REACTIVATED: 'Client Reactivated',
                        ADHOC_ISSUE_CREATED: 'Ad Hoc Issue Created',
                        ADHOC_ISSUE_UPDATED: 'Ad Hoc Issue Updated',
                        ADHOC_ISSUE_ASSIGNED: 'Ad Hoc Issue Assigned',
                        ADHOC_ISSUE_RESOLVED: 'Ad Hoc Issue Resolved',
                        ADHOC_ISSUE_SESSION_STARTED: 'Session Started',
                        ADHOC_ISSUE_SESSION_ENDED: 'Session Ended',
                        ADHOC_ISSUE_ASSIGNED_AND_UPDATED: 'Ad Hoc Issue Assigned + Updated',
                        ADHOC_ISSUE_RESOLVED_AND_UPDATED: 'Ad Hoc Issue Resolved + Updated',
                        ADHOC_ISSUE_ASSIGNED_RESOLVED_AND_UPDATED:
                          'Ad Hoc Issue Assigned + Resolved + Updated'
                      };
                      const actionLabel = actionLabelMap[log.action] || log.action;
                      const adHocIssueId = log.adHocIssue?.id;
                      const locationLabel = isClientActivity
                        ? log.client?.clientName || '-'
                        : log.adHocIssue?.locationPathString || log.adHocIssue?.description || '-';
                      return (
                        <Stack
                          key={log.id}
                          direction="row"
                          alignItems="center"
                          sx={{
                            borderBottom: '1px solid #eee',
                            padding: '6px 0',
                            minWidth: '800px'
                          }}>
                          <Typography width={200}>
                            {dayjs(log.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                          </Typography>
                          <Typography width={200}>{log.user?.name || '-'}</Typography>
                          <Typography
                            width={240}
                            sx={{
                              color: adHocIssueId ? '#0056D2' : 'inherit',
                              cursor: adHocIssueId ? 'pointer' : 'default',
                              textDecoration: adHocIssueId ? 'underline' : 'none'
                            }}
                            onClick={() => {
                              if (adHocIssueId) {
                                navigate(`/issues?tab=adHocIssues&adHocIssueId=${adHocIssueId}`);
                              }
                            }}>
                            {locationLabel}
                          </Typography>
                          <Typography width={160}>{actionLabel}</Typography>
                        </Stack>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <Stack direction="row" sx={{ fontWeight: 700, minWidth: '1200px' }}>
                      <Typography width={180}>Date of Change</Typography>
                      <Typography width={180}>User</Typography>
                      <Typography width={180}>Client / Location</Typography>
                      <Typography width={180}>Asset name</Typography>
                      {uniqueFields.map((field) => (
                        <Typography key={field} width={180}>
                          {fieldPathToLabel(field)}
                        </Typography>
                      ))}
                    </Stack>
                    {selectedItem?.stack?.map((log) => (
                      <Stack
                        key={log.id}
                        direction="row"
                        alignItems="center"
                        sx={{
                          borderBottom: '1px solid #eee',
                          padding: '6px 0',
                          minWidth: '1200px'
                        }}>
                        <Typography width={180}>
                          {dayjs(log.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                        </Typography>
                        <Typography width={180}>{log.user?.name || '-'}</Typography>
                        <Typography
                          width={200}
                          sx={{ color: '#0056D2', cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() => {
                            const roomId = log.submitted?.room?.id;
                            const client = log.submitted?.room?.client;
                            if (roomId)
                              navigate(
                                `/clients/${client?.clientName}/${client?.id}/location/${roomId}/room`
                              );
                          }}>
                          {log.submitted?.room?.name || '-'}
                        </Typography>
                        <Typography width={180}>{log.asset?.name || '-'}</Typography>
                        {uniqueFields.map((field) => {
                          const matched = convertDiff(log).find((d) => d.field === field);
                          return (
                            <Typography
                              key={field}
                              width={180}
                              sx={{
                                color: matched ? '#2e7d32' : 'inherit',
                                fontWeight: matched ? 600 : 400
                              }}>
                              {matched?.new ?? '-'}
                            </Typography>
                          );
                        })}
                      </Stack>
                    ))}
                  </>
                )}
              </Stack>
            </Stack>
          );
        })()}
      </Dialog>
    </WrapperPage>
  );
};

const WrapperPage = styled(Stack)(() => ({
  padding: '40px 30px 50px 40px',
  width: '100%',
  minHeight: '100vh',
  gap: '20px'
}));

const TypographyTitle = styled(Typography)(() => ({
  fontSize: '24px',
  fontWeight: 800,
  color: '#226AAA'
}));
